<template>
  <div id="course-menu-content">
    <!-- Display any feedback alert -->
    <v-alert v-if="feedback" type="warning" class="ma-8">{{
      feedback
    }}</v-alert>

    <!-- Header section for "CURRENT COURSE" title and close button -->
    <v-list class="header">
      <v-list-item density="compact" class="pa-0 px-4">
        <template #default>
          <div class="d-flex align-center w-100">
            <v-list-item-title class="current-course-title mb-0">
              {{ $t('courseDrawer.currentCourse.title') }}
            </v-list-item-title>

            <!-- Shows loading progress bar if course data is being fetched -->
            <v-progress-linear
              v-if="loadingCourse"
              color="#A5D867"
              indeterminate
              rounded
              class="short-loader ml-2"
            />

            <v-spacer />

            <v-btn
              icon
              variant="plain"
              color="#A5D867"
              @click.stop="
                () => {
                  courseDrawer = !courseDrawer
                  feedback = ''
                }
              "
            >
              <v-icon>mdi-window-close</v-icon>
            </v-btn>
          </div>
        </template>
      </v-list-item>
    </v-list>

    <!-- Active (non-archived) verified courses section -->
    <v-list
      v-if="
        userActiveCourse.length > 0 && userActiveCourse[0].status == 'verified'
      "
    >
      <!-- Loop through and display each active course -->
      <v-list
        v-model:opened="openedGroups"
        density="compact"
        class="py-0 pl-1 current-year"
      >
        <v-list-group
          v-for="course in userActiveCourse"
          :key="`course-${course.storageID || course.id}`"
          :value="`course-${course.storageID || course.id}`"
          append-icon=""
          @click="getActiveMarker('active_el', course.id)"
          class="current-year"
          :class="course.baseYear"
          :base-color="yearColor(course.baseYear)"
        >
          <template #activator="{ props, isOpen }">
            <v-list-item v-bind="props" lines="two">
              <template #prepend>
                <v-icon
                  size="31"
                  :class="[course.baseYear, { 'rotate-180': isOpen }]"
                >
                  mdi-menu-down
                </v-icon>
              </template>

              <v-list-item-title class="base-year" :class="course.baseYear">
                {{ getYearName(course.baseYear, course.requiredLanguage) }}
              </v-list-item-title>
              <v-list-item-subtitle class="course-title">
                {{ course.title }}
              </v-list-item-subtitle>
              <template #append></template>
            </v-list-item>
          </template>

          <!-- Course-level actions: archive + full-course PDF (aligned row) -->
          <div
            v-if="
              (getRoles && getRoles.includes('tester')) ||
              (getFeatures && getFeatures.includes('course-pdf'))
            "
            class="d-flex flex-wrap align-center ga-3 ml-14 mt-4 mb-2"
          >
            <v-tooltip
              v-if="getRoles && getRoles.includes('tester')"
              location="right"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="x-small"
                  elevation="0"
                  class="unit-icon flex-shrink-0"
                  color="rgb(56, 57, 53)"
                  @click.stop="archiveCourse(course)"
                >
                  {{ $t('courseDrawer.currentCourse.archiveButton') }}
                </v-btn>
              </template>
              <span>{{
                $t('courseDrawer.currentCourse.archiveTooltip', {
                  title: course.title,
                })
              }}</span>
            </v-tooltip>
            <v-tooltip location="right">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="outlined"
                  size="x-small"
                  class="unit-icon flex-shrink-0"
                  :class="course.baseYear"
                  :href="downloadCoursePdfURL(course.storageID)"
                  target="_blank"
                >
                  {{ $t('courseDrawer.currentCourse.downloadCourse') }}
                </v-btn>
              </template>
              <span>{{
                $t('courseDrawer.currentCourse.downloadCourseTooltip')
              }}</span>
            </v-tooltip>
          </div>

          <!-- Sections -->
          <v-list
            v-model:opened="openedGroups"
            density="compact"
            class="py-0 pl-1 course-sections"
          >
            <v-list-group
              v-for="child in course.sections"
              :key="`section-${course.storageID || course.id}-${child.id}`"
              :value="`section-${course.storageID || course.id}-${child.id}`"
              append-icon=""
              :base-color="yearColor(course.baseYear)"
            >
              <!-- Section Header -->
              <template #activator="{ props, isOpen: childIsOpen }">
                <v-list-item v-bind="props" class="mb-2">
                  <template #prepend>
                    <v-icon
                      size="31"
                      :class="[course.baseYear, { 'rotate-180': childIsOpen }]"
                    >
                      mdi-menu-down
                    </v-icon>
                  </template>

                  <v-list-item-title class="current-course-section-title">
                    {{ child.title }}
                  </v-list-item-title>
                  <template #append></template>
                </v-list-item>
              </template>

              <!-- PDF Download Button (Visible to testers or if 'section-pdf' is enabled) -->
              <template
                v-if="
                  (getRoles && getRoles.includes('tester')) ||
                  (getFeatures && getFeatures.includes('section-pdf'))
                "
              >
                <v-tooltip location="right">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      variant="outlined"
                      size="x-small"
                      class="mb-2 unit-icon"
                      :style="{
                        'margin-left': 'calc(16px + var(--indent-padding))',
                      }"
                      :class="course.baseYear"
                      :href="downloadPdfURL(course.storageID, child.id)"
                      target="_blank"
                    >
                      {{ $t('courseDrawer.currentCourse.downloadSection') }}
                    </v-btn>
                  </template>
                  <span>{{ $t('courseDrawer.currentCourse.downloadSectionTooltip') }}</span>
                </v-tooltip>
              </template>

              <!-- Units -->
              <v-list-item
                v-for="grandchild in child.units"
                :key="grandchild.id"
                density="compact"
                class="pl-14 current-course-units"
                style="padding-inline: 0"
                @click="selectUnit(grandchild.id, course.storageID, child.id)"
                :base-color="yearColor(course.baseYear)"
              >
                <template #prepend>
                  <v-icon
                    v-if="
                      activeUnit == grandchild.id &&
                      course.storageID == selectedCourseID
                    "
                    class="grn-bookmark"
                  >
                    bookmarkgrn
                  </v-icon>
                </template>

                <v-list-item-title
                  class="current-course-unit-title text-caption pl-2"
                  :class="{ active: activeUnit == grandchild.id }"
                >
                  {{
                    capitalize(
                      getUnitTitle(
                        grandchild,
                        child.requiredLanguage || course.requiredLanguage
                      )
                    )
                  }}
                </v-list-item-title>
              </v-list-item>
            </v-list-group>
          </v-list>
        </v-list-group>
      </v-list>
    </v-list>

    <!-- Show when no verified active courses are available -->
    <v-list v-else>
      <v-list-item density="compact" class="pa-0 px-4">
        <v-skeleton-loader
          :loading="loadingCourse"
          height="94"
          type="list-item-two-line"
        >
          <div v-if="loadFailed">
            <v-alert
              color="#592892"
              class="text-center font-weight-bold mb-2"
            >
              {{ $t('courseDrawer.currentCourse.loadFailed') }}
            </v-alert>
            <v-btn
              variant="flat"
              block
              color="#A5D867"
              @click="retryActiveCourse"
            >
              {{ $t('courseDrawer.currentCourse.tryAgain') }}
            </v-btn>
          </div>
          <v-btn
            v-else-if="
              currentCourse.length > 0 &&
              !journeyIsBuilt &&
              currentCourse[0].status === 'verified'
            "
            variant="flat"
            block
            color="#A5D867"
            :disabled="loadingCourse"
            @click="initUsersCourse"
          >
            {{ $t('loginScreen.startJourney') }}
          </v-btn>
          <v-alert
            v-else-if="!loadingCourse"
            color="#592892"
            class="text-center font-weight-bold mb-1"
          >
            {{ $t('courseDrawer.currentCourse.noApprovedCourse') }}
            <div v-if="getRoles && getRoles.includes('leader')" class="mt-4">
              <router-link to="/courses" custom v-slot="{ navigate }">
                <v-btn
                  variant="flat"
                  block
                  color="#A5D867"
                  @click="navigate"
                  role="link"
                >
                  {{ $t('courseDrawer.currentCourse.createCourse') }}
                </v-btn>
              </router-link>
            </div>
          </v-alert>
          <div v-else class="py-4"></div>
        </v-skeleton-loader>
      </v-list-item>
    </v-list>

    <v-divider
      class="dashed mx-10 mt-4 mb-4"
      style="margin-top: 15px"
    ></v-divider>

    <!-- Section header for previous (archived) courses -->
    <v-list class="header">
      <v-list-item density="compact" class="pa-0 px-4">
        <v-list-item-title>{{ $t('courseDrawer.previousCourses.title') }}</v-list-item-title>
      </v-list-item>
    </v-list>

    <!-- Loop through and display each archived course -->
    <v-list v-model:opened="openedGroups" @update:opened="onOpened">
      <v-list-group
        v-for="course in archivedUserCourses"
        :key="`course-${course.storageID || course.id}`"
        :value="`course-${course.storageID || course.id}`"
        append-icon=""
        @click="getActiveMarker('archive_active_el', course.id)"
        class="current-year"
        :class="course.baseYear"
        :base-color="yearColor(course.baseYear)"
      >
        <template #activator="{ props, isOpen }">
          <v-list-item v-bind="props" lines="two">
            <!-- custom colored arrow -->
            <template #prepend>
              <v-icon
                size="31"
                :class="[course.baseYear, { 'rotate-180': isOpen }]"
              >
                mdi-menu-down
              </v-icon>
            </template>

            <v-list-item-title class="base-year" :class="course.baseYear">
              {{ getYearName(course.baseYear, course.requiredLanguage) }}
            </v-list-item-title>
            <v-list-item-subtitle class="course-title">
              {{ course.title }}
            </v-list-item-subtitle>

            <!-- override append slot (empty to remove it) -->
            <template #append></template>
          </v-list-item>
        </template>

        <!-- Sections -->
        <v-list
          v-model:opened="openedGroups"
          density="compact"
          class="py-0 pl-1 course-sections"
        >
          <v-list-group
            v-for="child in course.sections"
            :key="`section-${course.storageID || course.id}-${child.id}`"
            :value="`section-${course.storageID || course.id}-${child.id}`"
            append-icon=""
            :base-color="yearColor(course.baseYear)"
          >
            <!-- Section Header -->
            <template #activator="{ props, isOpen: childIsOpen }">
              <v-list-item v-bind="props" class="mb-2">
                <!-- colored arrow for sections -->
                <template #prepend>
                  <v-icon
                    size="31"
                    :class="[course.baseYear, { 'rotate-180': childIsOpen }]"
                  >
                    mdi-menu-down
                  </v-icon>
                </template>

                <v-list-item-title class="current-course-section-title">
                  {{ child.title }}
                </v-list-item-title>

                <!-- override append slot (empty to remove it) -->
                <template #append></template>
              </v-list-item>
            </template>

            <!-- PDF Download Button (Visible to testers or if 'section-pdf' is enabled) -->
            <template
              v-if="
                (getRoles && getRoles.includes('tester')) ||
                (getFeatures && getFeatures.includes('section-pdf'))
              "
            >
              <v-tooltip location="right">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    variant="outlined"
                    size="x-small"
                    class="mb-2 unit-icon"
                    :style="{
                      'margin-left': 'calc(16px + var(--indent-padding))',
                    }"
                    :class="course.baseYear"
                    :href="downloadPdfURL(course.storageID, child.id)"
                    target="_blank"
                  >
                    {{ $t('courseDrawer.currentCourse.downloadSection') }}
                  </v-btn>
                </template>
                <span>{{
                  $t('courseDrawer.currentCourse.downloadSectionTooltip')
                }}</span>
              </v-tooltip>
            </template>

            <!-- Units -->
            <v-list-item
              v-for="grandchild in child.units"
              :key="grandchild.id"
              density="compact"
              class="pl-14 current-course-units"
              style="padding-inline: 0"
              @click="selectUnit(grandchild.id, course.storageID, child.id)"
              :base-color="yearColor(course.baseYear)"
            >
              <template #prepend>
                <v-icon
                  v-if="
                    activeUnit == grandchild.id &&
                    course.storageID == selectedCourseID
                  "
                  class="grn-bookmark"
                >
                  bookmarkgrn
                </v-icon>
              </template>

              <v-list-item-title
                class="current-course-unit-title text-caption pl-2"
                :class="{ active: activeUnit == grandchild.id }"
              >
                {{ capitalize(grandchild.title) }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
        </v-list>
      </v-list-group>
    </v-list>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import axios from 'axios'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { capitalize, interpret } from '@/helpers/stringUtils'
  import { matchesConfirmWord } from '@/helpers/confirmWord'
  import {
    localizeWeekForLanguage,
    stampWeekParagraphIds,
  } from '@/helpers/localizeWeekContent'

  export default {
    name: 'CurrentCourseMenu',
    components: {},
    data() {
      return {
        msg: null,
        feedback: null,
        active_el: 0,
        archive_active_el: 0,
        activeMarker: false,
        archiveMarker: false,
        subGroupMarker: false,
        userActiveCourse: [],
        usersActiveGroup: [],
        currentCourse: [],
        selectedCourseID: null,
        archivedUserCourses: [],
        loadingCourse: false,
        // The user's active course exists but could not be loaded (#115)
        loadFailed: false,
        journeyIsBuilt: false,
        isTester: null,
        openedGroups: [],
      }
    },
    watch: {
      courseDrawer: function (newVal) {
        if (newVal) {
          this.loadingCourse = true
          // this.retrieveActiveCourse()
          this.getGroup()

          // if we already have loaded archived courses don't do it again
          if (this.archivedUserCourses.length == 0) this.getArchivedCourse()
        }
      },
      '$route.params.id': {
        handler: function (search) {
          // console.log(search)
          if (search) this.init()
        },
        deep: true,
        immediate: true,
      },
      '$route.name'(n) {
        if (n === 'Welcome') {
          // collapse archived courses (and their sections)
          this.openedGroups = []
          // reset the arrow marker for archived list
          this.archive_active_el = 0
          // optional: clear selection markers related to archived view
          this.selectedCourseID = null
          this.activeUnit = null
        }
      },
      cid: function (newVal) {
        if (newVal) this.getGroup()
      },
      archivedUserCourses(newVal) {
        if (newVal.length) {
          console.log(
            '[watch] archivedUserCourses populated, initializing menu'
          )
          this.expandMenuFromRoute()
        }
      },
      userActiveCourse(newVal) {
        if (newVal.length) {
          console.log('[watch] userActiveCourse populated, initializing menu')
          this.expandMenuFromRoute()
        }
      },
    },
    computed: {
      ...mapState('UserData', ['user', 'profileComplete']),
      ...mapState('AppData', ['courseDrawer']),
      ...mapGetters('AppData', [
        'getChurchById',
        'getCourse',
        'getSections',
        'getMissions',
        'cid',
        'getActiveCourse',
        'getFeatures',
      ]),
      ...mapGetters('UserData', ['getRoles']),
      // Helper to get year name in course's original language
      // Legacy courses (no requiredLanguage) default to English
      getYearName() {
        return (baseYear, requiredLanguage) => {
          return interpret(baseYear, requiredLanguage || 'en')
        }
      },
      activeUnit: {
        get() {
          return this.$store.getters['AppData/activeUnit']
        },
        set(value) {
          this.$store.dispatch('AppData/setActiveUnit', value)
        },
      },
      courseDrawer: {
        get() {
          return this.$store.getters['AppData/courseDrawer']
        },
        set(value) {
          this.$store.dispatch('AppData/courseDrawer', value)
        },
      },
    },
    methods: {
      capitalize,
      interpret,
      getUnitTitle(unit, requiredLanguage) {
        if (!unit) return ''
        const lang = requiredLanguage || 'en'
        const localizedTitle =
          unit.localizedContent &&
          unit.localizedContent[lang] &&
          unit.localizedContent[lang].title
        if (localizedTitle && localizedTitle.trim()) {
          return localizedTitle
        }
        return unit.title || ''
      },
      localizeUnitForLanguage(unit, requiredLanguage) {
        if (!unit) return unit
        const rawLang = (requiredLanguage || 'en').toLowerCase()
        const lang = rawLang.startsWith('pt') ? 'pt' : rawLang
        const lc =
          (unit.localizedContent && unit.localizedContent[lang]) ||
          (lang === 'pt' && unit.pt ? unit.pt : null)
        const merged = { ...unit }
        const textFields = [
          'title',
          'subtitle',
          'description',
          'listenLabel',
          'completeLabel',
          'continueLearningLabel',
          'listen',
          'complete',
          'continueLearning',
          'truthBodyContent',
          'truthInBible',
          'equipBodyContent',
          'accountBodyContent',
          'footnotesBodyContent',
          'copyrightBodyContent',
        ]
        if (lc) {
          textFields.forEach((field) => {
            if (typeof lc[field] === 'string' && lc[field].trim() !== '') {
              merged[field] = lc[field]
            }
          })
        }

        const arrayFields = [
          'truthVersesQuotes',
          'truthStudyQuestions',
          'equipStudyQuestions',
          'accountStudyQuestions',
        ]
        if (lc) {
          arrayFields.forEach((field) => {
            if (Array.isArray(lc[field]) && lc[field].length > 0) {
              merged[field] = lc[field]
            }
          })
        }

        // Prevent root assignment bleed; only use localizedContent
        const assignmentFields = [
          'listenLabel',
          'completeLabel',
          'continueLearningLabel',
          'listen',
          'complete',
          'continueLearning',
        ]
        assignmentFields.forEach((field) => {
          if (lc && field in lc) {
            merged[field] = lc[field] || ''
          } else {
            merged[field] = ''
          }
        })

        return merged
      },
      yearColor(baseYear) {
        switch (baseYear) {
          case 'redYear':
            return '#6E273D' // matches your CSS
          case 'greenYear':
            return '#024731'
          case 'blueYear':
            return '#002B45'
          default:
            return '' // fallback
        }
      },
      onOpened(values) {
        // keep only the last toggled item
        this.openedGroups = values.length ? [values[values.length - 1]] : []
      },
      async archiveCourse(course) {
        this.feedback = ''

        if (!course) {
          await Swal.fire({
            title: this.$t('notifications.errors.houston'),
            text: this.$t('courseDrawer.currentCourse.archiveMissing'),
            confirmButtonClass:
              'v-btn v-btn--contained v-theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        try {
          const confirmed = await confirmAction({
            title: this.$t('courses.archiveTitle', { title: course.title }),
            html: this.$t('courses.archiveInstruction'),
            input: 'text',
            inputPlaceholder: this.$t('courses.archivePlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (
                  matchesConfirmWord(
                    value,
                    'ARCHIVE',
                    this.$t('courses.archivePlaceholder')
                  )
                ) {
                  resolve()
                } else {
                  resolve(this.$t('courses.archiveValidation'))
                }
              })
            },
            confirmButtonText: this.$t('courses.archiveConfirm'),
            cancelButtonText: this.$t('courses.cancelKeep'),
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (!confirmed) return

          const collections = ['currentcourses']
          let batch = db.batch()

          for (const element of collections) {
            let elementsRef = db
              .collection(element)
              .where('cid', '==', course.cid)
              .where('courseID', '==', course.id)

            const snapshot = await elementsRef.get()
            snapshot.forEach((doc) => {
              batch.update(doc.ref, { status: 'archived' })
            })

            await batch.commit()
          }

          this.feedback = this.$t('courses.courseArchived', {
            title: course.title,
          })
          this.archivedUserCourses = []

          setTimeout(() => {
            this.courseDrawer = !this.courseDrawer
          }, 10)
        } catch (error) {
          console.error('Error archiving the course:', error)

          this.$store.dispatch('AppData/logError', {
            message: 'Error archiving the course',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'archiveCourse',
            additionalInfo: {
              courseTitle: course.title,
              courseID: course.id,
            },
          })

          await Swal.fire({
            title: this.$t('notifications.errors.houston'),
            text: this.$t('courseDrawer.currentCourse.archiveError'),
            confirmButtonClass:
              'v-btn v-btn--contained v-theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      getActiveMarker(which, el) {
        // Ensure 'which' is a data property. In Vue 3 `this` is a proxy with no
        // own properties, so check $data.
        if (Object.prototype.hasOwnProperty.call(this.$data, which)) {
          this[which] = this[which] === el ? 0 : el
        } else {
          console.error(`Invalid property: ${which}`)
        }
      },
      init() {
        // Extract route params for unit, course, and section
        let unitID = this.$route.params.id
        let courseID = this.$route.params.courseID
        let sectionID = this.$route.params.sectionID

        // Ensure all necessary params are present before proceeding
        if (unitID && courseID && sectionID) {
          // If the current route is not 'Pdf', call the selectUnit method
          if (this.$route.name !== 'Pdf') {
            try {
              this.selectUnit(unitID, courseID, sectionID)
            } catch (error) {
              // Log the error using Vuex store dispatch
              this.$store.dispatch('AppData/logError', {
                message: 'Error selecting unit during initialization',
                stack: error.stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'init',
                additionalInfo: {
                  unitID: unitID || 'unknown',
                  courseID: courseID || 'unknown',
                  sectionID: sectionID || 'unknown',
                  userId: this.user ? this.user.uid : 'unknown',
                  userEmail: this.user ? this.user.email : 'unknown',
                },
              })
              console.error('Error in init function:', error)
            }
          }
        } else {
          // Log missing parameters as a warning
          const missingParams = {
            unitID: unitID || 'missing',
            courseID: courseID || 'missing',
            sectionID: sectionID || 'missing',
          }
          this.$store.dispatch('AppData/logError', {
            message: 'Missing route parameters during initialization',
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'init',
            additionalInfo: {
              missingParams: missingParams,
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          console.warn('Missing route parameters:', missingParams)
        }
      },
      async getAllTheUnits() {
        // Initialize an empty array to hold all the units
        let allOfTheUnits = []
        let unitRef = db.collection('units')

        try {
          // Fetch all units from Firestore
          let allUnitsSnapShot = await unitRef.get()

          // Map through each document and push it to the units array
          allUnitsSnapShot.docs.map((doc) => {
            let u = doc.data()
            u.id = doc.id
            allOfTheUnits.push(u)
          })

          // Optionally, log the number of units fetched for debugging
          // console.log('Total units fetched: ', allOfTheUnits.length);
        } catch (err) {
          // Log the error using Vuex store dispatch for centralized logging
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching all units from Firestore',
            stack: err.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'getAllTheUnits',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })

          // Optionally, log the error to the console for immediate visibility
          console.error('Error fetching all the units:', err)
        }

        // Return the array of units (empty if an error occurred)
        return allOfTheUnits
      },
      async getAllTheWeeks() {
        // Initialize an empty array to hold all the weeks
        let allOfTheWeeks = []
        let weekRef = db.collection('weeks')

        try {
          // Fetch all weeks from Firestore
          let allWeeksSnapShot = await weekRef.get()

          // Map through each document and push it to the weeks array
          allWeeksSnapShot.docs.map((doc) => {
            let w = doc.data()
            w.id = doc.id
            allOfTheWeeks.push(w)
          })

          // Optionally, log the number of weeks fetched for debugging
          // console.log('Total weeks fetched: ', allOfTheWeeks.length);
        } catch (err) {
          // Log the error using Vuex store dispatch for centralized logging
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching all weeks from Firestore',
            stack: err.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'getAllTheWeeks',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })

          // Optionally, log the error to the console for immediate visibility
          console.error('Error fetching all the weeks:', err)
        }

        // Return the array of weeks (empty if an error occurred)
        return allOfTheWeeks
      },
      async fetchAllSections() {
        try {
          let secs = []
          const roles = this.getRoles || []
          const userCid = this.user?.cid
          const isSuper =
            Array.isArray(roles) &&
            (roles.includes('superAdmin') || roles.includes('helpDesk'))
          let sectionRef = db.collection('sections')
          let snapshot = await sectionRef.get()
          snapshot.forEach((doc) => {
            let s = doc.data()
            s.id = doc.id
            const canView = isSuper
              ? true
              : (s.type === 'public' && s.status === 'verified') ||
                (s.cid && userCid && s.cid === userCid)
            if (canView) secs.push(s)
          })
          return secs
        } catch (error) {
          console.error('Error fetching sections:', error)
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching sections',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'fetchAllSections',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          throw new Error('Unable to fetch sections')
        }
      },
      async fetchAllMissions() {
        try {
          const missions = []
          const missionRef = db.collection('missions')
          const snapshot = await missionRef.get()
          snapshot.forEach((doc) => {
            const m = doc.data()
            m.id = doc.id
            missions.push(m)
          })
          return missions
        } catch (error) {
          console.error('Error fetching missions:', error)
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching missions',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'fetchAllMissions',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          throw new Error('Unable to fetch missions')
        }
      },
      async getArchivedCourse() {
        // Reset the archived courses array
        this.archivedUserCourses = []

        try {
          let currentCourseRef = db
            .collection('currentcourses')
            .where('cid', '==', this.user.cid)
            .where('user', '==', this.user.uid)

          // Fetch courses that match the query
          let activeRef = await currentCourseRef.get()

          if (!activeRef.empty) {
            // Array of promises to fetch all data in parallel
            let fetchPromises = activeRef.docs.map(async (doc) => {
              let course = doc.data()
              course.id = doc.id

              const storage = firebase.storage()
              const storageRef = storage.ref()

              // Step 1: Check storageRef validity
              if (!course.storageRef || typeof course.storageRef !== 'string') {
                console.warn(
                  `[❌ SKIP] Missing or invalid storageRef for: ${
                    course.title || course.id
                  }`
                )
                return
              }

              // Step 2: Check missions path
              if (!course.missions || typeof course.missions !== 'string') {
                console.warn(
                  `[❌ SKIP] Missing or invalid missions path for: ${
                    course.title || course.id
                  }`
                )
                return
              }

              try {
                const objectRef = storageRef.child(course.storageRef)
                const missionRef = storageRef.child(course.missions)

                const [courseUrl, missionUrl] = await Promise.all([
                  objectRef.getDownloadURL(),
                  missionRef.getDownloadURL(),
                ])

                const [resp, missionResp] = await Promise.all([
                  axios.get(courseUrl),
                  axios.get(missionUrl),
                ])

                if (!resp.data || !missionResp.data) {
                  console.warn(
                    `[⚠️ Warning] Missing data in response for: ${
                      course.title || course.id
                    }`
                  )
                  return
                }

                resp.data.storageID = course.storageID
                resp.data.missions = missionResp.data

                if (course.status === 'archived') {
                  // console.log('✅ Adding archived course:', resp.data.id)
                  this.archivedUserCourses.push(resp.data)
                } else {
                  console.log(
                    'ℹ️ Skipping non-archived course:',
                    resp.data.title
                  )
                }
              } catch (err) {
                console.error(
                  `❌ Error processing course: ${course.title || course.id}`,
                  err
                )

                this.$store.dispatch('AppData/logError', {
                  message:
                    'Error retrieving course files from Firebase Storage',
                  stack: err.stack,
                  timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                  component: 'CurrentCourseMenu.vue',
                  function: 'getArchivedCourse',
                  additionalInfo: {
                    courseId: course.id,
                    storageRef: course.storageRef,
                    missions: course.missions,
                    userId: this.user ? this.user.uid : 'unknown',
                    userEmail: this.user ? this.user.email : 'unknown',
                  },
                })
              }
            })

            // Wait for all promises to resolve
            await Promise.all(fetchPromises)

            this.archivedUserCourses.sort((a, b) => {
              const dateDiff = new Date(b.startDate) - new Date(a.startDate) // ⬅ reversed
              if (dateDiff !== 0) return dateDiff

              const titleA = a.title?.toLowerCase() || ''
              const titleB = b.title?.toLowerCase() || ''
              const titleDiff = titleA.localeCompare(titleB)
              if (titleDiff !== 0) return titleDiff

              const idA = a.storageID || a.id || ''
              const idB = b.storageID || b.id || ''
              return idA.localeCompare(idB)
            })

            this.expandMenuFromRoute()

            // console.log(
            //   '📦 Final archived courses array:',
            //   this.archivedUserCourses
            // )
          }
        } catch (err) {
          console.error('Error fetching archived courses:', err)
          // Optional: You could log this error to your logging system like before
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching archived courses',
            stack: err.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'getArchivedCourse',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
        }
      },
      expandMenuFromRoute() {
        const { courseID, sectionID, id: unitID } = this.$route.params

        if (!courseID || !sectionID || !unitID) {
          console.warn('[initMenuFromRoute] Missing one or more route params')
          return
        }

        const allCourses = [
          ...(this.userActiveCourse || []),
          ...(this.archivedUserCourses || []),
        ]

        for (const course of allCourses) {
          const currentCourseID = course.storageID || course.id
          const isMatch = currentCourseID === courseID
          if (!isMatch) continue

          for (const section of course.sections || []) {
            if (section.id !== sectionID) continue

            for (const unit of section.units || []) {
              if (unit.id !== unitID) continue

              const courseKey = `course-${currentCourseID}`
              const sectionKey = `section-${currentCourseID}-${section.id}`

              this.$nextTick(() => {
                this.openedGroups = [courseKey, sectionKey]
                this.selectedCourseID = currentCourseID
                this.activeUnit = unit.id
              })

              return // stop once we find a match
            }
          }
        }

        console.warn('[initMenuFromRoute] No match found for:', {
          courseID,
          sectionID,
          unitID,
        })
      },
      // Whether this user already has an enrollment in the course: active, or
      // being created right now (another tab or device). Read from the server.
      async hasEnrollmentFor(courseID) {
        const snap = await db
          .collection('currentcourses')
          .where('cid', '==', this.user.cid)
          .where('user', '==', this.user.uid)
          .where('status', 'in', ['active', 'initializing'])
          .get({ source: 'server' })
        const recent = Date.now() - 10 * 60 * 1000
        return snap.docs.some((doc) => {
          const data = doc.data()
          if (data.courseID === courseID && data.status === 'active') return true
          const created = data.created?.toMillis?.() || 0
          return data.status === 'initializing' && created > recent
        })
      },
      retryActiveCourse() {
        this.loadFailed = false
        this.loadingCourse = true
        this.retrieveActiveCourse()
      },
      async initUsersCourse() {
        this.loadingCourse = true
        this.journeyIsBuilt = false

        // Never create a second enrollment in the same course (#115). If the
        // check itself fails, offer a retry rather than risk a duplicate.
        const courseID = this.currentCourse[0]?.id
        try {
          if (courseID && (await this.hasEnrollmentFor(courseID))) {
            this.retrieveActiveCourse()
            return
          }
        } catch (error) {
          console.warn('Could not check for an existing enrollment:', error)
          this.loadFailed = true
          this.loadingCourse = false
          return
        }

        // console.log('initUsersCourse')

        try {
          // // Step 1: Fetch units and weeks
          // const [allUnits, allWeeks] = await Promise.all([
          //   this.getAllTheUnits(),
          //   this.getAllTheWeeks(),
          // ]).catch((error) => {
          //   this.$store.dispatch('AppData/logError', {
          //     message: 'Error fetching units or weeks',
          //     stack: error.stack,
          //     timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          //     component: 'CurrentCourseMenu.vue',
          //     function: 'initUsersCourse',
          //     additionalInfo: {
          //       userId: this.user ? this.user.uid : 'unknown',
          //       userEmail: this.user ? this.user.email : 'unknown',
          //     },
          //   })
          //   throw new Error('Unable to fetch units or weeks')
          // })

          // // Step 2: Clone current course, sections, and missions
          // let currentCourse =
          //   this.currentCourse.length > 0
          //     ? JSON.parse(JSON.stringify(this.currentCourse[0]))
          //     : {} // Fallback if no currentCourse exists

          // const allSections = JSON.parse(
          //   JSON.stringify(this.getSections[0] || [])
          // )
          // const allMissions = JSON.parse(
          //   JSON.stringify(this.getMissions[0] || [])
          // )
          // Step 1: Load all catalogs up front (directly from Firestore)
          const [allUnits, allWeeks, allSections, allMissions] =
            await Promise.all([
              this.getAllTheUnits(),
              this.getAllTheWeeks(),
              this.fetchAllSections(),
              this.fetchAllMissions(),
            ]).catch((error) => {
              this.$store.dispatch('AppData/logError', {
                message: 'Error fetching units/weeks/sections/missions',
                stack: error.stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'initUsersCourse',
                additionalInfo: {
                  userId: this.user ? this.user.uid : 'unknown',
                  userEmail: this.user ? this.user.email : 'unknown',
                },
              })
              throw new Error('Unable to fetch required catalogs')
            })

          // Step 2: Clone current course
          let currentCourse =
            this.currentCourse.length > 0
              ? JSON.parse(JSON.stringify(this.currentCourse[0]))
              : {}

          // Preconditions
          if (
            !Array.isArray(currentCourse.sections) ||
            !Array.isArray(currentCourse.units)
          ) {
            this.feedback =
              'This course is missing its structure (sections/units). Please contact your church admin.'
            this.loadingCourse = false
            return
          }

          if (!allSections.length) {
            this.feedback =
              'System data (sections) is not available. Please refresh and try again.'
            this.loadingCourse = false
            return
          }

          // Step 3: Validate fetched data
          if (Object.keys(currentCourse).length === 0) {
            console.warn(
              'No existing currentCourse found. Proceeding with an empty base course.'
            )
          }

          // If allUnits or allWeeks is missing, log error
          if (!allUnits || !allWeeks) {
            this.$store.dispatch('AppData/logError', {
              message: 'Invalid data during course initialization',
              stack: new Error('Missing data').stack,
              timestamp: firebase.firestore.FieldValue.serverTimestamp(),
              component: 'CurrentCourseMenu.vue',
              function: 'initUsersCourse',
              additionalInfo: {
                currentCourse,
                allUnits,
                allWeeks,
                userId: this.user ? this.user.uid : 'unknown',
                userEmail: this.user ? this.user.email : 'unknown',
              },
            })

            this.feedback =
              'There was an error creating your Journey. Try refreshing the page and/or logging out, clearing your cache, and logging back in. If this continues, please contact your church admin.'
            return
          }

          // Step 4: Create placeholder entry in the database
          let currentCourseRef
          let placeholderId = null

          try {
            currentCourseRef = await db.collection('currentcourses').add({
              cid: this.user.cid,
              user: this.user.uid,
              status: 'initializing',
              created: firebase.firestore.FieldValue.serverTimestamp(),
              lastUpdate: firebase.firestore.FieldValue.serverTimestamp(),
            })
            // ✅ fix the bug: use currentCourseRef.id (not ref.id)
            placeholderId = currentCourseRef.id

            // Sanity check
            if (!currentCourseRef || !currentCourseRef.id) {
              this.$store.dispatch('AppData/logError', {
                message:
                  'Firestore returned an undefined reference ID while creating course',
                stack: new Error('Firestore document reference is undefined')
                  .stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'initUsersCourse',
                additionalInfo: {
                  userId: this.user ? this.user.uid : 'unknown',
                  userEmail: this.user ? this.user.email : 'unknown',
                  firestoreRef: currentCourseRef,
                },
              })
              this.feedback = this.$t('courseDrawer.currentCourse.initError')
              throw new Error('Firestore document reference is undefined.')
            }

            // Step 5: Merge sections, units, and weeks (unchanged from your code)
            const mergedSections = currentCourse.sections.map((a1) => {
              const matchedSection = allSections.find((a2) => a1.id === a2.id)
              if (!matchedSection) {
                console.warn(
                  `No matching section found for section ID: ${a1.id}`
                )
              }
              return Object.assign(a1, matchedSection)
            })

            const mergedUnits = mergedSections.map((s) =>
              (s.units || []).map((a1) => {
                const matchedUnit = allUnits.find((a2) => a1.id === a2.id)
                if (!matchedUnit) {
                  console.warn(`No matching unit found for unit ID: ${a1.id}`)
                }
                return Object.assign(a1, matchedUnit)
              })
            )

            // Apply localized unit fields based on section/course language
            mergedUnits.forEach((unitsForSection, sectionIndex) => {
              const sectionLang =
                mergedSections[sectionIndex]?.requiredLanguage ||
                currentCourse.requiredLanguage ||
                'en'
              unitsForSection.forEach((unit, unitIndex) => {
                unitsForSection[unitIndex] = this.localizeUnitForLanguage(
                  unit,
                  sectionLang
                )
              })
            })
            // Ensure merged sections carry localized unit objects
            mergedSections.forEach((section, sectionIndex) => {
              section.units = mergedUnits[sectionIndex] || []
            })
            currentCourse.sections = mergedSections

            const unitLanguageById = {}
            mergedSections.forEach((section) => {
              const sectionLang =
                section.requiredLanguage ||
                currentCourse.requiredLanguage ||
                'en'
              ;(section.units || []).forEach((unit) => {
                if (unit?.id) {
                  unitLanguageById[unit.id] = sectionLang
                }
              })
            })
            currentCourse.units.forEach((unit) => {
              if (unit?.id && !unitLanguageById[unit.id]) {
                unitLanguageById[unit.id] =
                  currentCourse.requiredLanguage || 'en'
              }
            })

            const numWeeks = currentCourse.units.length
            let currentMission =
              currentCourse.mission?.length > 0
                ? currentCourse.mission
                : allMissions.filter(
                    (m) => !m.cid && m.weeks.length === numWeeks
                  )

            // ✅ Add guard here
            if (!Array.isArray(currentMission) || !currentMission.length) {
              this.$store.dispatch('AppData/logError', {
                message: 'No matching mission found',
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'initUsersCourse',
                additionalInfo: { numWeeks, courseId: currentCourse.id },
              })
              if (placeholderId) {
                await db
                  .collection('currentcourses')
                  .doc(placeholderId)
                  .delete()
              }
              this.feedback =
                'No matching mission found for this course length.'
              this.loadingCourse = false
              return
            }

            const currentWeeks = currentMission.map((s) =>
              s.weeks.map((a1, weekIndex) => {
                const matchedWeek = allWeeks.find((a2) => a1.id === a2.id)
                if (!matchedWeek) {
                  console.warn(`No matching week found for week ID: ${a1.id}`)
                }
                let week = Object.assign(a1, matchedWeek)
                const unitID = currentCourse.units[weekIndex]?.id
                const requiredLanguage =
                  (unitID && unitLanguageById[unitID]) ||
                  currentCourse.requiredLanguage ||
                  'en'
                week = localizeWeekForLanguage(week, requiredLanguage)
                return week
              })
            )

            // Step 6: Post-process + cleanup (unchanged)
            currentWeeks.forEach(function (week) {
              week.forEach(function (item, i) {
                const unitID = currentCourse.units[i]?.id || `unknown-id-${i}`
                item.unitID = unitID
                stampWeekParagraphIds(item, unitID)
                delete item.completionScore
              })
            })

            delete currentCourse.units
            mergedSections.forEach((s) => {
              delete s.category
              delete s.locked
              delete s.superLocked
            })
            mergedUnits.forEach((unit) => {
              unit.forEach((u) => {
                const fieldAbbreviations = {
                  accountBodyContent: 'abc',
                  equipBodyContent: 'ebc',
                  truthBodyContent: 'tbc',
                  truthInBible: 'ibc',
                  footnotesBodyContent: 'fbc',
                }
                ;[
                  'accountBodyContent',
                  'equipBodyContent',
                  'truthBodyContent',
                  'truthInBible',
                  'footnotesBodyContent',
                ].forEach((field) => {
                  if (u[field]) {
                    const contentArray = u[field].split('<p>')
                    const abbreviation = fieldAbbreviations[field] || field
                    u[field] = contentArray
                      .map(
                        (content, index) =>
                          `<p id="${u.id}-${abbreviation}-${index}">${content}`
                      )
                      .join('')
                  }
                })
                delete u.category
              })
            })

            // Step 7: Prepare final course object
            const finalCourseObject = {
              ...currentCourse,
              missions: JSON.parse(JSON.stringify(currentWeeks[0])),
            }

            // Step 8: Upload JSONs to Storage
            const jsonString = JSON.stringify(finalCourseObject)
            const jsonMissionString = JSON.stringify(currentWeeks[0])

            const blob = new Blob([jsonString], { type: 'application/json' })
            const missionBlob = new Blob([jsonMissionString], {
              type: 'application/json',
            })

            const storageRef = firebase.storage().ref()
            const coursePath = `/courses/${currentCourseRef.id}.json`
            const missionPath = `/missions/${currentCourseRef.id}.json`

            const [courseRef, missionRef] = await Promise.all([
              storageRef.child(coursePath).put(blob),
              storageRef.child(missionPath).put(missionBlob),
            ])

            const [courseDownloadURL, missionDownloadURL] = await Promise.all([
              courseRef.ref.getDownloadURL(),
              missionRef.ref.getDownloadURL(),
            ])

            // Step 9: Finalize placeholder → active
            await db.collection('currentcourses').doc(currentCourseRef.id).set(
              {
                courseID: currentCourse.id,
                storageID: currentCourseRef.id,
                course: courseDownloadURL,
                mission: missionDownloadURL,
                storageRef: courseRef.ref.fullPath,
                missions: missionRef.ref.fullPath,
                status: 'active',
                lastUpdate: firebase.firestore.FieldValue.serverTimestamp(),
              },
              { merge: true }
            )
            // 🔧 Optional: sweep stale placeholders for this user/cid
            try {
              const staleSnap = await db
                .collection('currentcourses')
                .where('cid', '==', this.user.cid)
                .where('user', '==', this.user.uid)
                .where('status', 'in', ['initializing', 'error'])
                .get()

              if (!staleSnap.empty) {
                const batch = db.batch()
                staleSnap.forEach((doc) => {
                  if (doc.id !== currentCourseRef.id) {
                    const d = doc.data()
                    if (!d.storageRef || !d.missions) {
                      batch.delete(doc.ref)
                    }
                  }
                })
                await batch.commit()
              }
            } catch (sweepErr) {
              this.$store.dispatch('AppData/logError', {
                message: 'Cleanup of stale currentcourses failed',
                stack: sweepErr.stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'initUsersCourse (cleanup)',
                additionalInfo: { userId: this.user?.uid, cid: this.user?.cid },
              })
            }

            // proceed with UI updates
            this.retrieveActiveCourse()
            this.journeyIsBuilt = true
            this.courseChanged = true
          } catch (error) {
            // 🔴 If anything after creating the placeholder fails, clean it up
            if (placeholderId) {
              // Choose A or B:
              // A) delete the placeholder
              await db.collection('currentcourses').doc(placeholderId).delete()

              // B) or keep as breadcrumb:
              // await db.collection('currentcourses').doc(placeholderId).set({
              //   status: 'error',
              //   errorMessage: error.message?.slice(0, 500) || 'unknown',
              //   lastUpdate: firebase.firestore.FieldValue.serverTimestamp(),
              // }, { merge: true })
            }

            this.$store.dispatch('AppData/logError', {
              message: 'Error uploading or finalizing course initialization',
              stack: error.stack,
              timestamp: firebase.firestore.FieldValue.serverTimestamp(),
              component: 'CurrentCourseMenu.vue',
              function: 'initUsersCourse',
              additionalInfo: {
                userId: this.user ? this.user.uid : 'unknown',
                userEmail: this.user ? this.user.email : 'unknown',
              },
            })
            this.feedback =
              'There was an error creating your Journey. Please contact your church admin.'
            this.loadingCourse = false
            return
          }
        } catch (error) {
          this.$store.dispatch('AppData/logError', {
            message: 'Critical error during course initialization',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'initUsersCourse',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          this.feedback =
            'An unexpected error occurred while initializing the course. Please try again later or contact support.'
        }
      },
      addUniqueIDsToContent(item, contentField, idPrefix) {
        if (item[contentField]) {
          const paragraphs = item[contentField].split('<p>')
          const newParagraphs = paragraphs.map((paragraph, index) =>
            paragraph.length > 0
              ? `<p id="${item.unitID}-${idPrefix}-${index}">${paragraph}`
              : ''
          )
          item[contentField] = newParagraphs.join('').toString()
        }
      },
      downloadPdfURL(courseID, sectionID) {
        try {
          if (this.user) {
            let href = '/sectionpdf'

            // Check if we are in development mode
            if (process.env.NODE_ENV === 'development') {
              href = '/sectionpdf' // Can keep it as is since both dev and prod point to the same URL
            }

            // Append courseID and sectionID to the URL
            href += `/${courseID}/${sectionID}`

            return href
          } else {
            // Log an error if user is missing
            throw new Error('User is null or undefined')
          }
        } catch (error) {
          // Log the error to Firestore
          this.$store.dispatch('AppData/logError', {
            message: 'Error generating PDF URL',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'downloadPdfURL',
            additionalInfo: {
              courseID,
              sectionID,
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })

          // Return an empty string if there is an error
          return ''
        }
      },
      downloadCoursePdfURL(courseID) {
        try {
          if (this.user) {
            let href = '/coursepdf'
            if (process.env.NODE_ENV === 'development') {
              href = '/coursepdf'
            }
            href += `/${courseID}`
            return href
          } else {
            throw new Error('User is null or undefined')
          }
        } catch (error) {
          this.$store.dispatch('AppData/logError', {
            message: 'Error generating course PDF URL',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'downloadCoursePdfURL',
            additionalInfo: {
              courseID,
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          return ''
        }
      },
      logSelectUnitError(error, unitID, courseID, sectionID) {
        const isQuotaError =
          error?.name === 'QuotaExceededError' ||
          (typeof error?.message === 'string' &&
            /quota|storage/i.test(error.message))

        this.$store.dispatch('AppData/logError', {
          message: isQuotaError
            ? 'selectUnit: localStorage quota exceeded while saving active unit'
            : 'Error in selectUnit function',
          stack: error?.stack,
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          component: 'CurrentCourseMenu.vue',
          function: 'selectUnit',
          additionalInfo: {
            unitID,
            courseID,
            sectionID,
            errorName: error?.name || 'unknown',
            userId: this.user ? this.user.uid : 'unknown',
            userEmail: this.user ? this.user.email : 'unknown',
          },
        })
      },
      selectUnit(unitID, courseID, sectionID) {
        this.selectedCourseID = courseID

        const locale = this.$route.params?.locale
        const route = locale
          ? {
              name: 'Unit__loc',
              params: { locale, courseID, sectionID, id: unitID },
            }
          : {
              name: 'Unit',
              params: { courseID, sectionID, id: unitID },
            }

        this.$router.push(route).catch((error) => {
          if (error?.name === 'NavigationDuplicated') return
          this.logSelectUnitError(error, unitID, courseID, sectionID)
          this.feedback =
            'Error selecting the unit. Please try again or contact support.'
        })

        try {
          this.$store.dispatch('AppData/setActiveUnit', unitID || null)
        } catch (error) {
          this.logSelectUnitError(error, unitID, courseID, sectionID)
        }
      },
      async retrieveActiveCourse() {
        // try {
        //     this.feedback = null;
        //     this.userActiveCourse = [];

        //     console.warn(`🔄 Attempting to fetch active courses... (Retries left: ${retries})`);

        //     const cid = this.$store.getters["AppData/cid"];
        //     if (!this.user || !cid) {
        //         console.warn("❌ Missing user or CID when retrieving active course.");
        //         this.feedback = "Could not retrieve your active course. Please try again.";
        //         return;
        //     }

        //     const response = await this.$store.dispatch("AppData/getActiveCourse", { cid, user: this.user });

        //     if (response && response.length > 0) {
        //         this.$set(this, "userActiveCourse", response); // Ensure Vue reactivity
        //         this.dispatchCourses();
        //         console.warn("✅ Successfully retrieved active courses.");
        //     } else {
        //         console.warn("⚠️ No active courses found.");

        //         if (retries > 0) {
        //             console.warn(`🔄 Retrying in ${delay / 1000} seconds... Attempts left: ${retries}`);
        //             await new Promise(resolve => setTimeout(resolve, delay));
        //             return this.retrieveActiveCourse(retries - 1, delay);
        //         }

        //         console.warn("🚨 No active courses found after retries. Proceeding with course creation.");

        //         // 🔥 **Trigger course creation**
        //         console.warn("📌 Attempting to initialize a new course...");
        //         await this.initUsersCourse();  // ✅ Ensure this gets called
        //     }
        // } catch (error) {
        //     if (retries > 0) {
        //         console.warn(`🔄 Retrying request... Attempts left: ${retries}`);
        //         await new Promise(resolve => setTimeout(resolve, delay));
        //         return this.retrieveActiveCourse(retries - 1, delay);
        //     }

        //     this.feedback = "Error retrieving active courses. Please check your connection.";
        //     console.error("🔥 Final error retrieving active courses:", error);

        //     this.$store.dispatch("AppData/logError", {
        //         message: "Error retrieving active courses",
        //         stack: error.stack,
        //         timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        //         component: "CurrentCourseMenu.vue",
        //         function: "retrieveActiveCourse",
        //         additionalInfo: {
        //             userId: this.user ? this.user.uid : "unknown",
        //             userEmail: this.user ? this.user.email : "unknown",
        //             cid: this.$store.getters["AppData/cid"],
        //         }
        //     });
        // }
        try {
          // Dispatch the getActiveCourse action
          this.$store
            .dispatch(
              'AppData/getActiveCourse',
              { cid: this.$store.getters['AppData/cid'], user: this.user },
              { root: true }
            )
            .then(() => {
              this.loadFailed = false
              this.userActiveCourse = this.getActiveCourse // Set active courses

              // Proceed to dispatch courses
              this.dispatchCourses()
            })
            .catch((error) => {
              // Log error in case of failure
              this.$store.dispatch('AppData/logError', {
                message: 'Error retrieving active courses',
                stack: error.stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'retrieveActiveCourse',
                additionalInfo: {
                  cid: this.$store.getters['AppData/cid'],
                  userId: this.user ? this.user.uid : 'unknown',
                  userEmail: this.user ? this.user.email : 'unknown',
                },
              })

              // Offer a retry; "Start Your Journey" here would create a
              // second enrollment (#115).
              this.loadFailed = true
              this.loadingCourse = false
            })
        } catch (error) {
          // Fallback for any unexpected error in the try block
          this.$store.dispatch('AppData/logError', {
            message: 'Unexpected error in retrieveActiveCourse',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'retrieveActiveCourse',
            additionalInfo: {
              cid: this.$store.getters['AppData/cid'],
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
        }
      },
      dispatchCourses() {
        try {
          // Dispatch the getCourse action
          this.$store
            .dispatch(
              'AppData/getCourse',
              { cid: this.$store.getters['AppData/cid'], userid: this.user },
              { root: true }
            )
            .then(() => {
              // Filter out archived courses and set the current course for the active group
              this.currentCourse = this.getCourse.filter(
                (c) =>
                  !c.archive &&
                  c.selectedGroup[0].id == this.usersActiveGroup[0].id
              )

              // Mark course loading as finished
              this.loadingCourse = false
            })
            .catch((error) => {
              // Log error in case of failure
              this.$store.dispatch('AppData/logError', {
                message: 'Error dispatching courses',
                stack: error.stack,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                component: 'CurrentCourseMenu.vue',
                function: 'dispatchCourses',
                additionalInfo: {
                  cid: this.$store.getters['AppData/cid'],
                  userId: this.user ? this.user.uid : 'unknown',
                  userEmail: this.user ? this.user.email : 'unknown',
                },
              })

              // Optionally, set feedback or handle the error state in UI
              this.feedback = this.$t(
                'courseDrawer.currentCourse.loadCoursesError'
              )
              this.loadingCourse = false
            })
        } catch (error) {
          // Fallback for any unexpected error in the try block
          this.$store.dispatch('AppData/logError', {
            message: 'Unexpected error in dispatchCourses',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'dispatchCourses',
            additionalInfo: {
              cid: this.$store.getters['AppData/cid'],
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
            },
          })
          this.loadingCourse = false // Ensure loading state is reset
        }
      },
      async getGroup() {
        try {
          if (this.user) {
            const cid = this.$store.getters['AppData/cid']
            let groupsRef = db.collection('groups').where('cid', '==', cid)

            // Fetch groups
            const snapshot = await groupsRef.get()

            // Process the fetched groups
            snapshot.forEach((doc) => {
              const groupData = doc.data()
              groupData.id = doc.id

              // Check if the user is either the leader or a member of the group
              if (
                groupData.leader === this.user.uid ||
                (groupData.members.length > 0 &&
                  groupData.members.includes(this.user.uid))
              ) {
                this.usersActiveGroup.push(groupData)
              }
            })

            if (this.usersActiveGroup.length > 0) {
              // Retrieve active courses if user is in any group
              this.retrieveActiveCourse()
            } else {
              this.feedback =
                'You are not part of any active group. Please contact your administrator.'
            }
          } else {
            this.feedback =
              'Houston we have a problem. Please try refreshing your browser. If the issue persists, try logging out and then back in.'
          }
        } catch (error) {
          // Log error if something goes wrong
          this.$store.dispatch('AppData/logError', {
            message: 'Error fetching user group',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CurrentCourseMenu.vue',
            function: 'getGroup',
            additionalInfo: {
              userId: this.user ? this.user.uid : 'unknown',
              userEmail: this.user ? this.user.email : 'unknown',
              cid: this.$store.getters['AppData/cid'],
            },
          })

          // Provide feedback to the user
          this.feedback =
            'There was an issue retrieving your group. Please try again later.'
        }
      },
    },
    created() {
      // console.log('user ', this.user, this.cid)
      // this.getGroup()
    },
  }
</script>

<style scoped>
  .v-list--dense .v-list-item .v-list-item__icon {
    width: auto;
    height: auto;
  }
  .v-list-item__icon {
    margin: 6px 0;
  }
  .v-list-item {
    font-size: 10px;
  }
  .v-list-item__action--stack {
    align-items: center;
  }
  #course-drawer .header .v-list-item-title {
    font-size: 15px;
    font-weight: 700;
  }
  #course-drawer .header .v-theme--light .v-list-item-title {
    color: rgba(0, 0, 0, 0.25);
  }
  #course-drawer .header .v-theme--dark .v-list-item-title {
    color: rgba(255, 255, 255, 0.5);
  }
  #course-drawer .header .mdi-window-close:before {
    font-weight: 700;
  }
  .current-year .v-list-item__icon.grn-bookmark {
    margin-right: 7px;
    margin-left: -14px;
    width: 7px;
    height: auto;
    min-width: 7px;
  }
  .archive-year .v-list-item {
    padding: 0 30px;
  }
  .current-course-units .v-list-item__content {
    padding: 6px 0;
  }
  .v-theme--dark .current-course-section-title,
  .current-year .v-theme--dark .v-list-item-subtitle.course-title {
    color: rgba(255, 255, 255, 0.5);
  }
  .current-course-title {
    flex: 0 0 auto;
  }
  .short-loader {
    flex: 0 0 30px;
  }
  .unit-icon.blueYear {
    border: 1px solid #002b45;
    background: transparent;
    color: #002b45;
  }
  .v-theme--dark .unit-icon.blueYear {
    border: 1px solid #5b6d81;
    background: transparent;
    color: #5b6d81;
  }
  .unit-icon.greenYear {
    border: 1px solid #024731;
    background: transparent;
    color: #024731;
  }
  .v-theme--dark .unit-icon.greenYear {
    border: 1px solid #5e8372;
    background: transparent;
    color: #5e8372;
  }
  .unit-icon.redYear {
    border: 1px solid #6e273d;
    background: transparent;
    color: #6e273d;
  }
  .v-theme--dark .unit-icon.redYear {
    border: 1px solid #a26f7b;
    background: transparent;
    color: #a26f7b;
  }
  /* .unit-icon.blueYear.active {
    background: #002B45;
    color: #fff;
}
.unit-icon.greenYear.active {
    background: #024731;
    color: #fff;
}
.unit-icon.redYear.active {
    background: #6E273D;
    color: #fff;
} */
  .unit-icon.blueYear:active:before,
  .unit-icon.blueYear:focus:before,
  .unit-icon.greenYear:active:before,
  .unit-icon.greenYear:focus:before,
  .unit-icon.redYear:active:before,
  .unit-icon.redYear:focus:before {
    opacity: 0;
  }
  /* Size+shape the colored circle on every group header */
  :deep(.v-list-group > .v-list-group__header > .v-list-item__prepend) {
    align-self: center;
    margin-right: 0;
    margin-left: 10px;
    height: 31px;
    width: 31px; /* ensure it’s a circle */
    min-width: 31px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* GREEN (light + dark) */
  :deep(
      .v-list-group.greenYear > .v-list-group__header > .v-list-item__prepend
    ) {
    background-color: #024731 !important;
  }
  .v-theme--dark
    :deep(
      .v-list-group.greenYear > .v-list-group__header > .v-list-item__prepend
    ) {
    background-color: #5e8372 !important;
  }

  /* RED (light + dark) */
  :deep(.v-list-group.redYear > .v-list-group__header > .v-list-item__prepend) {
    background-color: #6e273d !important;
  }
  .v-theme--dark
    :deep(
      .v-list-group.redYear > .v-list-group__header > .v-list-item__prepend
    ) {
    background-color: #a26f7b !important;
  }

  /* BLUE (light + dark) */
  :deep(
      .v-list-group.blueYear > .v-list-group__header > .v-list-item__prepend
    ) {
    background-color: #002b45 !important;
  }
  .v-theme--dark
    :deep(
      .v-list-group.blueYear > .v-list-group__header > .v-list-item__prepend
    ) {
    background-color: #5b6d81 !important;
  }

  /* Optional: tweak the chevron icon color inside the circle */
  :deep(.v-list-group > .v-list-group__header > .v-list-item__prepend .v-icon) {
    color: #ffffff;
    opacity: 1;
  }
  .course-sections
    :deep(
      .v-list-group > .v-list-group__header > .v-list-item__prepend .v-icon
    ) {
    color: #a5d867;
  }

  .current-course-units ::v-deep(.v-list-item__content) {
    margin-left: 0 !important;
  }
  /* === LIST GROUP HEADER (year row) === */
  .current-year :deep(.v-list-group__header .v-list-item-title.base-year) {
    text-transform: uppercase !important;
    font-weight: 700 !important;
    font-size: 25px; /* match v2 */
    line-height: 1.1;
  }

  /* Year color variants */
  .current-year .base-year.greenYear {
    color: #024731 !important;
  }
  .v-theme--dark .current-year .base-year.greenYear {
    color: #5e8372 !important;
  }

  .current-year .base-year.redYear {
    color: #6e273d !important;
  }
  .v-theme--dark .current-year .base-year.redYear {
    color: #a26f7b !important;
  }

  .current-year .base-year.blueYear {
    color: #002b45 !important;
  }
  .v-theme--dark .current-year .base-year.blueYear {
    color: #5b6d81 !important;
  }

  /* === SUBTITLE (course title under the year) === */
  .current-year
    :deep(.v-list-group__header .v-list-item-subtitle.course-title) {
    font-weight: 400;
    font-size: 14px;
    opacity: 1;
  }
  .current-year
    :deep(
      .v-theme--light.v-list-group__header .v-list-item-subtitle.course-title
    ) {
    /* lighter gray like v2 */
    color: rgba(0, 0, 0, 0.6) !important;
  }

  /* === SECTION HEADERS (inside expanded group) === */
  .current-year :deep(.v-list-group__items .current-course-section-title) {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 15px;
  }
  .current-year
    :deep(.v-list-group__items .v-theme--light .current-course-section-title) {
    color: #383935;
  }

  /* === UNIT HEADERS === */
  .current-year :deep(.v-list-group__items .current-course-unit-title) {
    color: rgba(255, 255, 255, 0.5);
  }
  .current-year
    :deep(.v-list-group__items .v-theme--light .current-course-unit-title) {
    color: #383935;
  }

  /* .v-list-group__items .v-list-item {
    padding-inline-start: 40px !important;
  } */

  /* keep the round colored prepend “dot” behavior you already restored */
  .current-year :deep(.v-list-item__prepend:first-child) {
    align-self: center;
    margin-right: 0;
    margin-left: 10px;
    height: 31px;
    min-width: 31px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  /* scoped in CurrentCourseMenu.vue */
  :deep(.v-list-item__prepend .v-list-item__spacer) {
    display: none;
  }
  :deep(.v-list-item__content) {
    margin-left: 16px; /* shifts all text right */
  }
  /* Year header (the v-list-group activator row) */
  .current-year :deep(.v-list-item--variant-text:hover .v-list-item__overlay) {
    background-color: #a5d867;
    opacity: 0.12;
  }
  .v-list-group--open.current-year
    > :deep(.v-list-group__header .v-list-item__overlay) {
    background-color: #a5d867;
    opacity: 0.12;
  }
  .current-course-units.v-list-item--density-compact.v-list-item--one-line {
    min-height: 0;
  }
  /* --- Stop the flash: neutralize both layers by default --- */
  .current-year :deep(.v-list-item__overlay),
  .current-year :deep(.v-list-item__underlay) {
    background-color: transparent !important;
    opacity: 0;
    transition: opacity 160ms ease, background-color 160ms ease;
  }

  /* Use a constant green overlay and just fade opacity on hover/open */
  .current-year :deep(.v-list-item__overlay) {
    background-color: #a5d867 !important; /* your green */
  }

  /* Hover states (headers + items) */
  .current-year :deep(.v-list-group__header:hover .v-list-item__overlay),
  .current-year
    :deep(.v-list-group__items .v-list-item:hover .v-list-item__overlay) {
    opacity: 0.12; /* intensity */
  }

  /* Open state for group header */
  .v-list-group--open.current-year
    > :deep(.v-list-group__header .v-list-item__overlay) {
    opacity: 0.12;
  }

  /* Optional: slightly higher intensity in dark theme */
  .v-theme--dark
    .current-year
    :deep(.v-list-group__header:hover .v-list-item__overlay),
  .v-theme--dark
    .current-year
    :deep(.v-list-group__items .v-list-item:hover .v-list-item__overlay),
  .v-theme--dark
    .v-list-group--open.current-year
    > :deep(.v-list-group__header .v-list-item__overlay) {
    opacity: 0.18;
  }

  :deep(.v-icon) {
    transition: transform 0.25s ease;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }
</style>
